// Firebase App (the core Firebase SDK) is always required and must be listed first
import firebase from 'firebase/compat/app';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';

import {
  getMultiFactorResolver,
  RecaptchaVerifier,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
} from 'firebase/auth';

// Add the Firebase products that you want to use
import 'firebase/compat/auth';

import { PROJECTS_ENDPOINT } from '../constants/EndPoints';

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  projectId: process.env.REACT_APP_PROJECT_ID,
};

let onAuthStateChangeListener = null;

const app = firebase.initializeApp(config);
const auth = getAuth(app);
const db = getFirestore(app, 'organizations-providers');

export { auth };
firebase.auth().onAuthStateChanged(function (user) {
  // console.log('onAuthStateChanged', user);
  if (onAuthStateChangeListener != null) {
    onAuthStateChangeListener.onAuthStateChanged(user);

    if (!user) {
      onAuthStateChangeListener.logout();
    } else {
      onAuthStateChangeListener.loggingIn(user);
    }
  }
});

export function setListener(listener) {
  onAuthStateChangeListener = listener;

  const auth = firebase.auth();
  const user = auth.currentUser;
  if (!user) {
    onAuthStateChangeListener.logout();
  } else {
    onAuthStateChangeListener.loggingIn(user);
  }
}

export function getCurrentUser() {
  const auth = firebase.auth();
  const user = auth.currentUser;
  return user;
}

export function signInWithEmailAndPassword(email, password) {
  return new Promise((resolve, reject) => {
    firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then((userCredential) => {
        resolve(userCredential.user);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export async function signInWithOkta(organizationCode) {
  const q = query(
    collection(db, 'provider-code'),
    where('organization_code', '==', organizationCode)
  );

  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return Promise.reject(new Error('organization code is invalid'));
  }

  const docData = querySnapshot.docs[0].data();

  const providerId = docData['provider_id'];

  if (!providerId) {
    return Promise.reject(new Error('provider id is invalid'));
  }

  const provider = new firebase.auth.OAuthProvider(providerId);
  return new Promise((resolve, reject) => {

    firebase
      .auth()
      .signInWithPopup(provider)
      .then((userCredential) => {
        resolve(userCredential.user);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function signOut() {
  return firebase.auth().signOut();
}

export function sendPasswordResetEmail(email) {
  return firebase.auth().sendPasswordResetEmail(email);
}

export function verifyPasswordResetCode(code) {
  return firebase.auth().verifyPasswordResetCode(code);
}

export function confirmPasswordReset(code, newPassword) {
  return firebase.auth().confirmPasswordReset(code, newPassword);
}

export function checkActionCode(code) {
  return firebase.auth().checkActionCode(code);
}

export function applyActionCode(code) {
  return firebase.auth().applyActionCode(code);
}
let recaptchaVerifier = null;
let recaptchaContainer = null;

// A grecaptcha widget can only ever be rendered once into a given element
// (reusing one throws "reCAPTCHA has already been rendered in this element"),
// so every verifier gets a freshly created container.
function getRecaptchaVerifier() {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch (e) {
      // instance already destroyed — nothing to clear
    }
    recaptchaVerifier = null;
  }
  if (recaptchaContainer && recaptchaContainer.parentNode) {
    recaptchaContainer.parentNode.removeChild(recaptchaContainer);
  }
  recaptchaContainer = document.createElement('div');
  recaptchaContainer.style.display = 'none';
  document.body.appendChild(recaptchaContainer);
  recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainer, {
    size: 'invisible',
  });
  return recaptchaVerifier;
}

// ---- MFA sign-in challenge (user already has enrolled factors) ----

let verificationId = null;
let multiFactorResolver = null;
let signInHint = null;

export const getMfaResolver = (error) => {
  multiFactorResolver = getMultiFactorResolver(auth, error);
  return multiFactorResolver;
};

export const startMfaSignin = async (multiFactorHint, session) => {
  signInHint = multiFactorHint;
  if (multiFactorHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
    const phoneInfoOptions = {
      multiFactorHint: multiFactorHint,
      session: session,
    };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      getRecaptchaVerifier()
    );
  }
  // TOTP factors need no server round-trip before code entry.
};

export const finishMfaSignIn = async (verificationCode) => {
  if (!multiFactorResolver || !signInHint) {
    throw new Error('No MFA sign-in in progress');
  }
  let multiFactorAssertion;
  if (signInHint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
    multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      signInHint.uid,
      verificationCode
    );
  } else {
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
  }
  try {
    await multiFactorResolver.resolveSignIn(multiFactorAssertion);
  } catch (error) {
    // Keep resolver state so the user can retry with a new code.
    console.error('MFA sign-in failed', error);
    throw new Error('Wrong MFA code. Please insert the correct code to login.');
  }
  multiFactorResolver = null;
  verificationId = null;
  signInHint = null;
};

// ---- MFA enrollment (login gate + wizard) ----

let enrollVerificationId = null;
let totpSecret = null;

export const checkMfaEnrollmentNeeded = async () => {
  const user = auth.currentUser;
  if (!user) {
    return { required: false };
  }
  if (multiFactor(user).enrolledFactors.length > 0) {
    // Already enrolled — Firebase challenged them during sign-in if required.
    return { required: false };
  }
  try {
    const token = await user.getIdToken();
    // The require-flag comes from the projects payload's userData
    // (mfa / mfaEnabled / PhoneNumber) — there is no per-user GET endpoint.
    const whitelabelId = process.env.REACT_APP_WHITELABEL_ID;
    const url = `${PROJECTS_ENDPOINT}/please${whitelabelId ? `?token3=${whitelabelId}` : ''
      }`;
    // Hard cap so a slow API can never delay login for long; fail open below.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    let response;
    try {
      response = await fetch(url, {
        headers: { token: token, Accept: 'application/json' },
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }
    if (!response.ok) {
      throw new Error(`projects fetch failed with status ${response.status}`);
    }
    const json = await response.json();
    const userData = json.data && json.data.userData;
    if (userData && userData.mfa) {
      return {
        required: true,
        details: { phoneNumber: userData.PhoneNumber || '', email: user.email },
      };
    }
    return { required: false };
  } catch (error) {
    // Fail open: a broken endpoint must not lock users out of the system.
    console.error('MFA enrollment check failed, continuing login', error);
    return { required: false };
  }
};

export const startPhoneEnrollment = async (phoneNumber) => {
  const user = auth.currentUser;
  const session = await multiFactor(user).getSession();
  const phoneAuthProvider = new PhoneAuthProvider(auth);
  enrollVerificationId = await phoneAuthProvider.verifyPhoneNumber(
    { phoneNumber: phoneNumber, session: session },
    getRecaptchaVerifier()
  );
};

export const finishPhoneEnrollment = async (verificationCode) => {
  const user = auth.currentUser;
  const cred = PhoneAuthProvider.credential(
    enrollVerificationId,
    verificationCode
  );
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  await multiFactor(user).enroll(assertion, 'SMS');
  enrollVerificationId = null;
};

export const startTotpEnrollment = async () => {
  const user = auth.currentUser;
  const session = await multiFactor(user).getSession();
  totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
  const qrUrl = totpSecret.generateQrCodeUrl(
    user.email,
    process.env.REACT_APP_SYSTEM_NAME || 'AQS'
  );
  return { secretKey: totpSecret.secretKey, qrUrl: qrUrl };
};

export const finishTotpEnrollment = async (verificationCode) => {
  const user = auth.currentUser;
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    totpSecret,
    verificationCode
  );
  await multiFactor(user).enroll(assertion, 'Authenticator app');
  totpSecret = null;
};

const OktaProvidersMap = {
  'aqs-sys': 'oidc.okta-stage',
}